import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { CalendarDays, MapPin, Ticket, ArrowRight, Mail } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { FadeUp } from "@/components/Reveal";
import { useLang } from "@/lib/i18n";
import { useState } from "react";
import { toast } from "sonner";

const MONTHS_ZH = ["1月", "2月", "3月", "4月", "5月", "6月", "7月", "8月", "9月", "10月", "11月", "12月"];

export default function UpcomingShows() {
  const { lang } = useLang();
  const zh = lang === "zh";
  const [email, setEmail] = useState("");
  const [subBusy, setSubBusy] = useState(false);
  const { data: events = [] } = useQuery({
    queryKey: ["events"],
    queryFn: () => api.get("/events").then((r) => r.data),
    staleTime: 60_000,
  });

  const subscribe = async (e) => {
    e.preventDefault();
    setSubBusy(true);
    try {
      await api.post("/subscribe", { email });
      toast.success(zh ? "订阅成功！新演出公布时会第一时间通知您。" : "Subscribed! We'll notify you when a new show is announced.");
      setEmail("");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubBusy(false);
    }
  };

  const subscribeForm = (
    <FadeUp className="mt-12 border border-[#ffde00]/40 bg-[#ffde00]/5 px-6 md:px-10 py-8 flex flex-col md:flex-row md:items-center gap-5 md:gap-10" >
      <div className="md:w-2/5">
        <p className="font-zh text-base md:text-lg text-[#f5f5f5] leading-relaxed">
          {zh ? (
            <>
              订阅演出日历，<span className="text-[#ffde00]">新演出公布第一时间通知您。</span>
            </>
          ) : (
            <>
              Subscribe to the show calendar —{" "}
              <span className="text-[#ffde00]">be the first to know about new shows.</span>
            </>
          )}
        </p>
      </div>
      <form onSubmit={subscribe} className="flex-1 flex flex-col sm:flex-row gap-3">
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={zh ? "您的邮箱 your@email.com" : "your@email.com"}
          className="dfe-input flex-1"
          data-testid="calendar-subscribe-email"
        />
        <button type="submit" disabled={subBusy} className="btn-yellow justify-center disabled:opacity-50" data-testid="calendar-subscribe-button">
          {subBusy ? (zh ? "订阅中…" : "SUBSCRIBING…") : zh ? "订阅通知" : "SUBSCRIBE"}
        </button>
      </form>
    </FadeUp>
  );

  const today = new Date().toISOString().slice(0, 10);
  const upcoming = events.filter((e) => (e.date || "") >= today);

  return (
    <section id="calendar" data-testid="upcoming-shows" className="py-24 md:py-32 bg-[#0a0a0a]">
      <div className="max-w-[1800px] mx-auto px-4 md:px-8">
        <FadeUp className="flex items-end justify-between gap-6 border-b border-white/10 pb-6 mb-10 md:mb-16">
          <div>
            <div className="font-mono2 text-xs text-[#ffde00] tracking-[0.3em] mb-3">03 — UPCOMING SHOWS</div>
            <h2 className="font-display text-4xl md:text-6xl uppercase leading-[0.95] text-[#f5f5f5]">{zh ? "演出日历" : "UPCOMING SHOWS"}</h2>
          </div>
          <CalendarDays size={32} className="text-[#ffde00] hidden md:block" />
        </FadeUp>

        {upcoming.length === 0 ? (
          <FadeUp className="border border-dashed border-white/15 py-16 md:py-24 text-center" data-testid="calendar-empty">
            <p className="font-display text-2xl md:text-4xl uppercase text-[#f5f5f5]">{zh ? "新一季演出即将公布" : "NEW SEASON LINE-UP COMING SOON"}</p>
            <p className="font-zh text-sm md:text-base text-[#a1a1aa] mt-4">
              {zh ? "想第一时间知道下一场演出？关注我们或直接洽谈合作。" : "Want to know about the next show first? Follow us or talk to us about bringing one to your city."}
            </p>
            <Link to="/contact" className="btn-yellow mt-8 inline-flex" data-testid="calendar-cta">
              {zh ? "商业合作" : "BUSINESS ENQUIRY"}
              <ArrowRight size={15} />
            </Link>
          </FadeUp>
        ) : (
          <div className="border-t border-white/10">
            {upcoming.map((e, i) => {
              const d = e.date ? new Date(e.date + "T00:00:00") : null;
              const day = d ? String(d.getDate()).padStart(2, "0") : "—";
              const mon = d ? (zh ? MONTHS_ZH[d.getMonth()] : d.toLocaleString("en", { month: "short" }).toUpperCase()) : "";
              return (
                <FadeUp key={e.id || i} delay={Math.min(i * 0.06, 0.3)}>
                  <div className="group grid grid-cols-12 gap-4 items-center py-6 md:py-8 border-b border-white/10 hover:bg-[#ffde00] transition-colors duration-300" data-testid={`event-row-${i}`}>
                    <div className="col-span-3 md:col-span-2 flex items-baseline gap-2 pl-2 md:pl-4">
                      <span className="font-display text-4xl md:text-6xl text-[#ffde00] group-hover:text-black leading-none transition-colors duration-300">{day}</span>
                      <span className="font-mono2 text-xs md:text-sm text-[#a1a1aa] group-hover:text-black/60 transition-colors duration-300">{mon}</span>
                    </div>
                    <div className="col-span-9 md:col-span-6">
                      <h3 className="font-display text-xl md:text-3xl uppercase text-[#f5f5f5] group-hover:text-black leading-tight transition-colors duration-300">{e.title}</h3>
                      <p className="font-zh text-xs md:text-sm text-[#a1a1aa] group-hover:text-black/70 mt-1.5 flex items-center gap-2 transition-colors duration-300">
                        {e.venue && (
                          <>
                            <MapPin size={13} /> {e.venue}
                          </>
                        )}
                        {e.city && <span>· {e.city}</span>}
                      </p>
                      {e.notes && <p className="font-zh text-xs text-[#52525b] group-hover:text-black/50 mt-1 transition-colors duration-300">{e.notes}</p>}
                    </div>
                    <div className="col-span-12 md:col-span-4 flex md:justify-end pr-2 md:pr-4">
                      {e.ticket_url && e.ticket_url.startsWith("http") && (
                        <a
                          href={e.ticket_url}
                          target="_blank"
                          rel="noreferrer"
                          data-testid={`event-ticket-${i}`}
                          className="btn-yellow !py-2.5 !px-5 group-hover:!bg-black group-hover:!text-[#ffde00] group-hover:!border-black"
                        >
                          <Ticket size={14} /> {zh ? "购票" : "TICKETS"}
                        </a>
                      )}
                    </div>
                  </div>
                </FadeUp>
              );
            })}
          </div>
        )}
        {subscribeForm}
      </div>
    </section>
  );
}
